import React, { useState, useEffect, useCallback } from "react";
import Select from "react-select";
import {
  getItems,
  addItem,
  updateItem,
  deleteItem,
  getSuppliers,
} from "../../services/apiService";
import { FaEdit, FaTrash, FaPlus } from "react-icons/fa";
import "../AddItem/AddItem.css";

const UNIT_OPTIONS = [
  "Kit",
  "Pack",
  "Vial",
  "Bottle",
  "Piece",
  "Set",
  "Box",
  "Canister",
];

const formatPrice = (value) => {
  const num = Number(value || 0);

  return num.toLocaleString("en-PH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

export default function AddItem() {
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [editingItemId, setEditingItemId] = useState(null);

  const [filter, setFilter] = useState("All");
  const [supplierFilter, setSupplierFilter] = useState("All");
  const [unitFilter, setUnitFilter] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 10;

  const [isModalOpen, setIsModalOpen] = useState(false);

  const [itemForm, setItemForm] = useState({
    CatalogNo: "",
    ItemName: "",
    GenericName: "",
    ItemPiece: 1,
    Unit: "Piece",
    IsImported: "Local",
    SupplierID: "",
    POPrice: "",
  });

  useEffect(() => {
    fetchItems();
    fetchSuppliers();
  }, []);

  const fetchItems = async () => {
    try {
      const data = await getItems();
      setItems(data || []);
      setFilteredItems(data || []);
    } catch (error) {
      console.error("Error fetching items:", error);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const data = await getSuppliers();
      setSuppliers(data || []);
    } catch (error) {
      console.error("Error fetching suppliers:", error);
    }
  };

  const resetFormOnly = () => {
    setEditingItemId(null);
    setItemForm({
      CatalogNo: "",
      ItemName: "",
      GenericName: "",
      ItemPiece: 1,
      Unit: "Piece",
      IsImported: "Local",
      SupplierID: "",
      POPrice: "",
    });
  };

  const closeModal = () => {
    resetFormOnly();
    setIsModalOpen(false);
  };

  const openAddModal = () => {
    resetFormOnly();
    setIsModalOpen(true);
  };

  const handleAddOrUpdateItem = async (e) => {
    e.preventDefault();

    try {
      const payload = {
        ...itemForm,
        ItemPiece: Number(itemForm.ItemPiece || 1),
        Unit: itemForm.Unit || "Piece",
        POPrice: itemForm.POPrice === "" ? 0 : Number(itemForm.POPrice),
      };

      if (editingItemId) {
        await updateItem(editingItemId, payload);
      } else {
        await addItem(payload);
      }

      closeModal();
      fetchItems();
    } catch (error) {
      console.error("Error saving item:", error);
      alert("Failed to save item.");
    }
  };

  const handleEdit = (item) => {
    setItemForm({
      CatalogNo: item.CatalogNo || "",
      ItemName: item.ItemName || "",
      GenericName: item.GenericName || "",
      ItemPiece: item.ItemPiece || 1,
      Unit: item.Unit || "Piece",
      IsImported: item.IsImported || "Local",
      SupplierID: item.SupplierID || "",
      POPrice: item.POPrice ?? "",
    });

    setEditingItemId(item.ItemId);
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this item?")) return;

    try {
      await deleteItem(id);
      const updated = items.filter((i) => i.ItemId !== id);
      setItems(updated);
      setFilteredItems(updated);
    } catch (error) {
      console.error("Error deleting item:", error);
      alert("Failed to delete item.");
    }
  };

  const applyFilter = useCallback(() => {
    let temp = [...items];

    if (filter !== "All") {
      temp = temp.filter((i) => i.IsImported === filter);
    }

    if (supplierFilter !== "All") {
      temp = temp.filter(
        (i) => Number(i.SupplierID) === Number(supplierFilter)
      );
    }

    if (unitFilter !== "All") {
      temp = temp.filter((i) => (i.Unit || "Piece") === unitFilter);
    }

    if (searchTerm.trim() !== "") {
      const q = searchTerm.toLowerCase();

      temp = temp.filter(
        (i) =>
          (i.ItemName || "").toLowerCase().includes(q) ||
          (i.CatalogNo || "").toLowerCase().includes(q) ||
          (i.GenericName || "").toLowerCase().includes(q) ||
          String(i.POPrice || "").toLowerCase().includes(q)
      );
    }

    setFilteredItems(temp);
  }, [items, filter, supplierFilter, unitFilter, searchTerm]);

  useEffect(() => {
    applyFilter();
    setCurrentPage(1);
  }, [applyFilter]);

  const totalPages = Math.ceil(filteredItems.length / ITEMS_PER_PAGE);

  const paginatedItems = filteredItems.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const supplierOptions = [
    { value: "All", label: "All Suppliers" },
    ...suppliers.map((s) => ({
      value: s.SupplierID,
      label: s.SupplierName,
    })),
  ];

  const modalSupplierOptions = suppliers.map((s) => ({
    value: s.SupplierID,
    label: s.SupplierName,
  }));

  return (
    <div className="add-item-container">
      <div className="page-header">
        <h1>ITEM MASTER LIST</h1>

        <button className="btn-add-item" onClick={openAddModal}>
          <FaPlus /> Add Item
        </button>
      </div>

      <div className="filter-container">
        <div className="filter-item full-row">
          <label>Search Item</label>
          <input
            type="text"
            placeholder="Search item name, catalog no, generic name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="filter-item">
          <label>Source</label>
          <select value={filter} onChange={(e) => setFilter(e.target.value)}>
            <option value="All">All</option>
            <option value="Local">Local</option>
            <option value="Foreign">Foreign</option>
          </select>
        </div>

        <div className="filter-item">
          <label>Unit</label>
          <select
            value={unitFilter}
            onChange={(e) => setUnitFilter(e.target.value)}
          >
            <option value="All">All</option>
            {UNIT_OPTIONS.map((u) => (
              <option key={u} value={u}>
                {u}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-item full-row">
          <label>Supplier</label>
          <Select
            classNamePrefix="supplier-select"
            options={supplierOptions}
            value={supplierOptions.find((o) => o.value === supplierFilter)}
            onChange={(opt) => setSupplierFilter(opt?.value || "All")}
            placeholder="Search supplier..."
            isSearchable
          />
        </div>
      </div>

      <table className="items-table">
        <thead>
          <tr>
            <th>Catalog No</th>
            <th>Item Name</th>
            <th>Generic</th>
            <th>Piece</th>
            <th>Unit</th>
            <th>PO Price</th>
            <th>Source</th>
            <th>Supplier</th>
            <th className="actions-header">Actions</th>
          </tr>
        </thead>

        <tbody>
          {paginatedItems.map((it) => (
            <tr key={it.ItemId}>
              <td>{it.CatalogNo}</td>
              <td>{it.ItemName}</td>
              <td>{it.GenericName || "—"}</td>
              <td>{Number(it.ItemPiece || 0).toLocaleString("en-PH")}</td>
              <td>{it.Unit || "Piece"}</td>
              <td>₱{formatPrice(it.POPrice)}</td>
              <td>{it.IsImported}</td>
              <td>
                {suppliers.find(
                  (s) => Number(s.SupplierID) === Number(it.SupplierID)
                )?.SupplierName || "—"}
              </td>

              <td className="actions-cell">
                <button
                  type="button"
                  className="action-btn edit"
                  onClick={() => handleEdit(it)}
                  title="Edit"
                >
                  <FaEdit />
                </button>

                <button
                  type="button"
                  className="action-btn delete"
                  onClick={() => handleDelete(it.ItemId)}
                  title="Delete"
                >
                  <FaTrash />
                </button>
              </td>
            </tr>
          ))}

          {paginatedItems.length === 0 && (
            <tr>
              <td colSpan="9" style={{ textAlign: "center", padding: "14px" }}>
                No items found.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {totalPages > 1 && (
        <div className="pagination">
          <button
            type="button"
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((p) => p - 1)}
          >
            Prev
          </button>

          <span>
            Page {currentPage} of {totalPages}
          </span>

          <button
            type="button"
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage((p) => p + 1)}
          >
            Next
          </button>
        </div>
      )}

      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <div>
                <h2>{editingItemId ? "Edit Item" : "Add Item"}</h2>
                <p className="modal-subtitle">
                  Enter item information and supplier details
                </p>
              </div>

              <button
                type="button"
                className="modal-close"
                onClick={closeModal}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleAddOrUpdateItem} className="add-item-form">
              <div className="form-row">
                <label>Catalog No</label>
                <input
                  value={itemForm.CatalogNo}
                  onChange={(e) =>
                    setItemForm({ ...itemForm, CatalogNo: e.target.value })
                  }
                  required
                />
              </div>

              <div className="form-row">
                <label>Item Name</label>
                <input
                  value={itemForm.ItemName}
                  onChange={(e) =>
                    setItemForm({ ...itemForm, ItemName: e.target.value })
                  }
                  required
                />
              </div>

              <div className="form-row">
                <label>Generic Name</label>
                <input
                  value={itemForm.GenericName}
                  onChange={(e) =>
                    setItemForm({ ...itemForm, GenericName: e.target.value })
                  }
                />
              </div>

              <div className="form-row">
                <label>Per Piece</label>
                <input
                  type="number"
                  min="1"
                  value={itemForm.ItemPiece}
                  onChange={(e) =>
                    setItemForm({ ...itemForm, ItemPiece: e.target.value })
                  }
                  required
                />
              </div>

              <div className="form-row">
                <label>Unit</label>
                <select
                  value={itemForm.Unit}
                  onChange={(e) =>
                    setItemForm({ ...itemForm, Unit: e.target.value })
                  }
                  required
                >
                  {UNIT_OPTIONS.map((u) => (
                    <option key={u} value={u}>
                      {u}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-row">
                <label>PO Price</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  value={itemForm.POPrice}
                  onChange={(e) =>
                    setItemForm({ ...itemForm, POPrice: e.target.value })
                  }
                />
              </div>

              <div className="form-row">
                <label>Source</label>
                <select
                  value={itemForm.IsImported}
                  onChange={(e) =>
                    setItemForm({ ...itemForm, IsImported: e.target.value })
                  }
                >
                  <option value="Local">Local</option>
                  <option value="Foreign">Foreign</option>
                </select>
              </div>

              <div className="form-row form-row-full">
                <label>Supplier</label>
                <Select
                  classNamePrefix="supplier-select"
                  options={modalSupplierOptions}
                  value={
                    modalSupplierOptions.find(
                      (o) => Number(o.value) === Number(itemForm.SupplierID)
                    ) || null
                  }
                  onChange={(opt) =>
                    setItemForm({ ...itemForm, SupplierID: opt?.value || "" })
                  }
                  placeholder="Search supplier..."
                  isSearchable
                />
              </div>

              <div className="form-buttons">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={closeModal}
                >
                  Cancel
                </button>

                <button type="submit" className="btn-save">
                  {editingItemId ? "Save Changes" : "Add Item"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}